import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";

type ParamType = "number" | "text" | "select" | "boolean" | "date";
const TYPE_LABELS: Record<ParamType, string> = {
  number: "Sayı", text: "Metin", select: "Seçim", boolean: "Evet/Hayır", date: "Tarih",
};
const TYPE_OPTS = Object.entries(TYPE_LABELS) as [ParamType, string][];

interface Props {
  open: boolean;
  centerId: string;
  centerName: string;
  onClose: () => void;
}

const numOrNull = (v: string): number | null => {
  const t = v.trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Bir üretim merkezinin parti parametre tanımlarını yönetir (ekle/düzenle/pasifleştir/sil). */
export function CenterParamDrawer({ open, centerId, centerName, onClose }: Props) {
  const qc = useQueryClient();
  const paramsQ = trpc.productionCenterParam.list.useQuery({ centerId }, { enabled: open && !!centerId, staleTime: 10_000 });
  const createMut = trpc.productionCenterParam.create.useMutation();
  const updateMut = trpc.productionCenterParam.update.useMutation();
  const toggleMut = trpc.productionCenterParam.toggleActive.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const deleteMut = trpc.productionCenterParam.delete.useMutation();

  const [editId, setEditId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [dataType, setDataType] = useState<ParamType>("number");
  const [unit, setUnit] = useState("");
  const [optionsText, setOptionsText] = useState("");
  const [required, setRequired] = useState(false);
  const [minVal, setMinVal] = useState("");
  const [maxVal, setMaxVal] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const params = (paramsQ.data ?? []) as Array<{
    id: string; label: string; dataType: ParamType; unit: string | null;
    options: string[]; required: boolean; minVal: number | null; maxVal: number | null; isActive: boolean;
  }>;
  const busy = createMut.isPending || updateMut.isPending;

  function resetForm() {
    setEditId(null); setLabel(""); setDataType("number"); setUnit("");
    setOptionsText(""); setRequired(false); setMinVal(""); setMaxVal(""); setErr(null);
  }
  useEffect(() => { if (open) resetForm(); /* eslint-disable-next-line */ }, [open, centerId]);

  function loadForEdit(p: (typeof params)[number]) {
    setEditId(p.id); setLabel(p.label); setDataType(p.dataType); setUnit(p.unit ?? "");
    setOptionsText((p.options ?? []).join("\n")); setRequired(p.required);
    setMinVal(p.minVal != null ? String(p.minVal) : ""); setMaxVal(p.maxVal != null ? String(p.maxVal) : "");
    setErr(null);
  }

  async function save() {
    setErr(null);
    if (!label.trim()) { setErr("Parametre adı zorunlu."); return; }
    const opts = dataType === "select" ? optionsText.split("\n").map((s) => s.trim()).filter(Boolean) : [];
    if (dataType === "select" && opts.length === 0) { setErr("Seçim tipi için en az bir seçenek girin (her satıra bir seçenek)."); return; }
    const payload = {
      label: label.trim(), dataType, unit: unit.trim() || null, options: opts, required,
      minVal: dataType === "number" ? numOrNull(minVal) : null,
      maxVal: dataType === "number" ? numOrNull(maxVal) : null,
    };
    try {
      if (editId) await updateMut.mutateAsync({ id: editId, ...payload });
      else await createMut.mutateAsync({ centerId, ...payload });
      await qc.invalidateQueries();
      resetForm();
    } catch (e) { setErr((e as Error).message); }
  }

  function remove(id: string) {
    deleteMut.mutate({ id }, {
      onError: (e) => setErr(e.message),
      onSuccess: () => { deleteMut.reset(); qc.invalidateQueries(); if (editId === id) resetForm(); },
    });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Parti Parametreleri"
      subtitle={centerName ? `Merkez: ${centerName}` : undefined}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      {/* Mevcut parametreler */}
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginBottom: 8 }}>
        Tanımlı Parametreler ({params.length})
      </div>
      {paramsQ.isLoading ? (
        <div style={{ padding: 12, color: "var(--neutral-500)", fontSize: 13 }}>Yükleniyor…</div>
      ) : params.length === 0 ? (
        <div style={{ padding: 12, color: "var(--neutral-500)", fontSize: 13 }}>Henüz parametre yok. Aşağıdan ekleyin (ör. Sıcaklık °C, pH).</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {params.map((p) => (
            <div key={p.id} className="d-card" style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 10, opacity: p.isActive ? 1 : 0.55 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {p.label}{p.required ? <span style={{ color: "var(--danger)" }}> *</span> : null}
                  {p.unit ? <span style={{ color: "var(--neutral-500)", fontWeight: 400 }}> ({p.unit})</span> : null}
                </div>
                <div style={{ fontSize: 11, color: "var(--neutral-500)" }}>
                  {TYPE_LABELS[p.dataType]}
                  {p.dataType === "select" && p.options.length ? ` · ${p.options.join(", ")}` : ""}
                  {p.dataType === "number" && (p.minVal != null || p.maxVal != null) ? ` · ${p.minVal ?? "−∞"}…${p.maxVal ?? "+∞"}` : ""}
                  {!p.isActive ? " · pasif" : ""}
                </div>
              </div>
              <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => loadForEdit(p)}><Icon name="edit" size={14} /></button>
              <button type="button" className={p.isActive ? "iact danger" : "iact success"} aria-label={p.isActive ? "Pasifleştir" : "Aktifleştir"} title={p.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: p.id, isActive: !p.isActive })} disabled={toggleMut.isPending}><Icon name={p.isActive ? "archive" : "check"} size={14} /></button>
              <button type="button" className="iact danger" aria-label="Sil" title="Sil" onClick={() => remove(p.id)} disabled={deleteMut.isPending}><Icon name="trash" size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Ekle / düzenle formu */}
      <div style={{ borderTop: "1px solid var(--neutral-200)", paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{editId ? "Parametreyi Düzenle" : "Yeni Parametre"}</div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cp-label">Parametre Adı <span className="req">*</span></label>
            <input id="cp-label" className="form-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="örn. Sıcaklık" maxLength={100} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cp-type">Tip <span className="req">*</span></label>
            <select id="cp-type" className="form-select" value={dataType} onChange={(e) => setDataType(e.target.value as ParamType)}>
              {TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cp-unit">Birim</label>
            <input id="cp-unit" className="form-input" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="°C, pH, %…" maxLength={20} />
          </div>
          <div className="form-field" style={{ justifyContent: "flex-end" }}>
            <label className="lbl" htmlFor="cp-req">Zorunlu mu?</label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, height: 38, fontSize: 13 }}>
              <input id="cp-req" type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
              <span>Parti girişinde doldurulması zorunlu</span>
            </label>
          </div>
        </div>
        {dataType === "select" ? (
          <div className="form-field">
            <label className="lbl" htmlFor="cp-opts">Seçenekler (her satıra bir tane) <span className="req">*</span></label>
            <textarea id="cp-opts" className="form-textarea" value={optionsText} onChange={(e) => setOptionsText(e.target.value)} rows={3} placeholder={"Düşük\nNormal\nYüksek"} />
          </div>
        ) : null}
        {dataType === "number" ? (
          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="cp-min">Min (opsiyonel)</label>
              <input id="cp-min" className="form-input" type="text" inputMode="decimal" value={minVal} onChange={(e) => setMinVal(e.target.value)} />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="cp-max">Max (opsiyonel)</label>
              <input id="cp-max" className="form-input" type="text" inputMode="decimal" value={maxVal} onChange={(e) => setMaxVal(e.target.value)} />
            </div>
          </div>
        ) : null}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button type="button" className="btn-md primary" onClick={save} disabled={busy}>
            <Icon name={editId ? "check" : "plus"} size={14} />{busy ? "Kaydediliyor…" : editId ? "Güncelle" : "Ekle"}
          </button>
          {editId ? <button type="button" className="btn-md ghost" onClick={resetForm} disabled={busy}>Vazgeç</button> : null}
        </div>
      </div>
    </Drawer>
  );
}
